import { Component, Input, OnInit } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { ButtonSeverity } from 'primeng/types/button';

export type DropdownPanelOptions = {
    labels: string[];
    classes: string[];
    severities: (ButtonSeverity | undefined)[];
    icons: string[];
    tooltips: string[];
    disabled: boolean[];
    functions: Array<() => Promise<void>>;
    showSpinner: boolean[];
    showDividers: boolean[];
    tooltipLinks: string[];
    tooltipLinkLabels: string[];
    tooltipAutoHide: boolean[];
};

export function getDropdownPanelOptions(overwrite?: Partial<DropdownPanelOptions>): DropdownPanelOptions {
    return {
        classes: overwrite?.classes ?? [],
        disabled: overwrite?.disabled ?? [],
        functions: overwrite?.functions ?? [],
        icons: overwrite?.icons ?? [],
        labels: overwrite?.labels ?? [],
        severities: overwrite?.severities ?? [],
        showDividers: overwrite?.showDividers ?? [],
        showSpinner: overwrite?.showSpinner ?? [],
        tooltips: overwrite?.tooltips ?? [],
        tooltipLinks: overwrite?.tooltipLinks ?? [],
        tooltipLinkLabels: overwrite?.tooltipLinkLabels ?? [],
        tooltipAutoHide: overwrite?.tooltipAutoHide ?? [],
    };
}

/**
 * @class DropdownPanelComponent
 * @description Generic reusable component for having a dropdown like button with an overlay panel like used for exports.
 *  Each entry in the panel can have its own label, class, icon, tooltip, disabled state, click handler and spinner handling.
 *  See for instance usage for exports.
 *
 *  The main button is configured via buttonLabel, buttonIcon.
 *  Buttons in the panel are configured via labels, classes, icons, etc.
 *  Each panel button will use the attributes of the index in the array.
 *
 *  For example:
 *  [labels]=['yours', 'mine']
 *  [functions]=[clickYours, clickMine]
 *
 *  In this case the first button in the panel will be called "yours" and call "await clickYours();" on click, the second button
 *  will be called "mine" and call "await clickMine();" on click.
 *
 * @component
 * @selector app-dropdown-panel
 * @example
 *   <app-dropdown-panel
 *       [labels]="['activities_pdf', 'activities_xls', 'activities_csv']"
 *       [classes]="['p-button-warn', 'p-button-success', '']"
 *       [severities]="['warn', 'success', undefined]"
 *       [icons]="['pi pi-file-pdf', 'pi pi-file-excel', 'pi pi-file']"
 *       [tooltips]="['activities_pdf_proFeature', 'activities_xls_proFeature', 'activities_csv_proFeature']"
 *       [disabled]="[!licenseStatePro, !licenseStatePro, !licenseStatePro]"
 *       [functions]="[exportTableToPdf, exportTableToExcel, exportTableToCsv]"
 *       [showSpinner]="[false, false, false]"
 *       [showDividers]="[false, true, false]"
 *       buttonLabel="export_action"
 *       buttonIcon="pi pi-chevron-down">
 *    </app-dropdown-panel>
 *
 * @remarks
 * - All input arrays must have the same length as the `functions` array, if they are set.
 * - Inputs are checked for their consistency. Please check the console for errors and warnings.
 * - Not provided optional input parameters will be filled with default values
 * - Use translation keys for labels and tooltips for i18n support.
 * - Prefer severity to class for styling. Set 'danger' severity instead of 'p-button-danger' class.
 */

@Component({
    selector: 'app-dropdown-panel',
    templateUrl: './dropdown-panel.component.html',
    styleUrls: ['./dropdown-panel.component.scss'],
    standalone: false,
})
export class DropdownPanelComponent implements OnInit {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    [key: string]: any; // allow member access via this[key]

    /** Array of translation keys for the labels of the panel buttons. (Required) */
    @Input() labels!: string[];
    /** Array of CSS classes to apply to each panel button for custom styling. */
    @Input() classes: string[] = [];
    /** Array of CSS severities to apply to each panel button for button style. Set undefined for the default style. */
    @Input() severities: (ButtonSeverity | undefined)[] = [];
    /** Array of CSS classes to apply to the main drop down button */
    @Input() buttonClasses: string[] = [];
    /** Array of icon class names (e.g., 'pi pi-file-pdf') for each panel button. */
    @Input() icons: string[] = [];
    /** Array of translation keys for tooltips, which are displayed for disabled panel buttons. */
    @Input() tooltips: string[] = [];
    /** Array of booleans to enable/disable each panel button. */
    @Input() disabled: boolean[] = [];
    /** Array of async functions to be called when a panel button is clicked. (Required) */
    @Input() functions!: Array<() => Promise<void>>;
    /** Array of booleans to show or hide a loading spinner for each panel button. */
    @Input() showSpinner: boolean[] = [];
    /** Array of booleans to show a divider after each panel button (default: false for all). */
    @Input() showDividers: boolean[] = [];
    /** Array of URL paths for a clickable link rendered inside the tooltip of a disabled panel button. */
    @Input() tooltipLinks: string[] = [];
    /** Array of translation keys for the labels of the links shown inside tooltips. */
    @Input() tooltipLinkLabels: string[] = [];
    /** Array of booleans controlling whether the tooltip auto-hides on mouse-out for each panel button (default: true). */
    @Input() tooltipAutoHide: boolean[] = [];
    /** Translation key for the main dropdown button label. (Required) */
    @Input() buttonLabel!: string;
    /** Icon class for the main dropdown button (default: 'pi pi-chevron-down'). */
    @Input() buttonIcon: string = 'pi pi-chevron-down';
    /** Optional text to be displayed at the top of the panel above the buttons. */
    @Input({ required: false }) topText?: string | undefined = undefined;

    // @ts-expect-error -- TODO check if translate is needed for the component to work!
    constructor(private readonly translate: TranslateService) {}

    ngOnInit(): void {
        // validate inputs and fill empty ones
        const fnCount = this.functions.length;
        this.fillArray('labels', fnCount, 'no labels provided');
        this.fillArray('classes', fnCount);
        this.fillArray('severities', fnCount);
        this.fillArray('icons', fnCount);
        this.fillArray('tooltips', fnCount);
        this.fillArray('disabled', fnCount, false);
        this.fillArray('showSpinner', fnCount, false);
        this.fillArray('showDividers', fnCount, false);
        this.fillArray('tooltipLinks', fnCount);
        this.fillArray('tooltipLinkLabels', fnCount);
        this.fillArray('tooltipAutoHide', fnCount, true);
    }

    private fillArray(arrayName: string, count: number, defaultValue: string | boolean = '', warning?: string): void {
        if (!this[arrayName] || this[arrayName].length === 0) {
            this[arrayName] = Array(count).fill(defaultValue);
            if (warning) {
                console.warn(`WARN - DropdownPanelComponent: ${warning}`);
            }
        } else if (this[arrayName].length !== count) {
            console.error(
                `ERROR - DropdownPanelComponent: array ${arrayName} must have the same entry count as functions array!`,
            );
        }
    }

    async click(index: number): Promise<void> {
        if (this.functions[index]) {
            await this.functions[index]();
        }
    }
}

export interface DropdownPanelConfig {
    labels: string[];
    classes: string[];
    severities: (ButtonSeverity | undefined)[];
    icons: string[];
    tooltips: string[];
    disabled: boolean[];
    functions: Array<() => Promise<void>>;
    showSpinner: boolean[];
    buttonLabel: string;
    buttonIcon: string;
    showDividers: boolean[];
    tooltipLinks: string[];
    tooltipLinkLabels: string[];
    tooltipAutoHide: boolean[];
}
